import { Link } from "@tanstack/react-router";
import { Menu, X } from "lucide-react";
import { useState } from "react";

import { LocaleSwitcher } from "@/components/index/locale-switcher";
import { cn } from "@/components/ui/lib/utils";
import { Skeleton } from "@/components/ui/skeleton";
import { UserDropdown } from "@/components/user-dropdown/user-dropdown";
import { useSession } from "@/lib/auth-client";
import * as m from "@/paraglide/messages";

export function LandingHeader() {
	const [mobileOpen, setMobileOpen] = useState(false);
	const { data: session, isPending } = useSession();
	const isLoggedIn = !!session?.user && !session.user.isAnonymous;
	const navLinks = [
		{ label: m.landing_header_features(), to: "/about" as const },
		{ label: m.landing_header_contact(), to: "/contact" as const },
	];

	return (
		<header className="sticky top-0 z-50 w-full bg-background/50 backdrop-blur-md">
			<div className="mx-auto flex items-center justify-between px-6 py-4 md:px-12 lg:px-[75px]">
				{/* Left nav */}
				<nav className="hidden items-center md:flex">
					{navLinks.map((link) => (
						<Link
							key={link.label}
							to={link.to}
							className="px-4 py-2 text-base font-semibold tracking-[0.5px] text-primary transition-opacity hover:opacity-70"
						>
							{link.label}
						</Link>
					))}
				</nav>

				{/* Logo (centered on desktop) */}
				<Link to="/" className="mr-auto flex items-center gap-2 md:mr-0">
					<img src="/logo.svg" alt="StudyOS" className="h-8 w-auto" />
					<span className="text-2xl font-semibold tracking-tight">
						<span className="text-primary">Study</span>
						<span className="font-light text-chart-5">OS</span>
					</span>
				</Link>

				{/* Right auth */}
				<div className="hidden items-center justify-end gap-4 md:flex">
					<LocaleSwitcher />
					{isPending ? (
						<>
							<Skeleton className="h-11 w-24 rounded-full" />
							<Skeleton className="size-9 rounded-full" />
						</>
					) : isLoggedIn ? (
						<>
							<Link
								to="/chat"
								search={{ redirect: "latest" }}
								className="rounded-full bg-primary px-6 py-2 text-base font-bold text-primary-foreground transition-opacity hover:opacity-90"
							>
								{m.landing_header_goToStudy()}
							</Link>
							<UserDropdown />
						</>
					) : (
						<>
							<Link
								to="/sign-in"
								className="rounded-full border border-primary px-6 py-2 text-base font-bold text-primary transition-colors hover:bg-primary/5"
							>
								{m.landing_header_signIn()}
							</Link>
							<Link
								to="/sign-up"
								className="rounded-full bg-primary px-6 py-2 text-base font-bold text-primary-foreground transition-opacity hover:opacity-90"
							>
								{m.landing_header_startFree()}
							</Link>
						</>
					)}
				</div>

				{/* Mobile toggle */}
				<button
					type="button"
					className="ml-auto inline-flex items-center justify-center rounded-md p-2 text-primary md:hidden"
					onClick={() => setMobileOpen(!mobileOpen)}
					aria-label={m.landing_header_toggleMenu()}
				>
					{mobileOpen ? <X className="size-5" /> : <Menu className="size-5" />}
				</button>
			</div>

			{mobileOpen && (
				<div className="border-t border-primary/10 bg-background px-6 py-4 md:hidden">
					<nav className="flex flex-col gap-1">
						{navLinks.map((link) => (
							<Link
								key={link.label}
								to={link.to}
								className="rounded-md px-3 py-2 text-base font-semibold tracking-[0.5px] text-primary hover:bg-primary/5"
								onClick={() => setMobileOpen(false)}
							>
								{link.label}
							</Link>
						))}
					</nav>
					<div className="mt-3 flex flex-col gap-2">
						{isLoggedIn ? (
							<Link
								to="/chat"
								className={cn(
									"w-full rounded-full bg-primary px-6 py-3 text-center text-base font-bold text-primary-foreground",
								)}
								onClick={() => setMobileOpen(false)}
							>
								{m.landing_header_goToStudy()}
							</Link>
						) : (
							<>
								<Link
									to="/sign-in"
									className="w-full rounded-full border border-primary px-6 py-3 text-center text-base font-bold text-primary"
									onClick={() => setMobileOpen(false)}
								>
									{m.landing_header_signIn()}
								</Link>
								<Link
									to="/sign-up"
									className="w-full rounded-full bg-primary px-6 py-3 text-center text-base font-bold text-primary-foreground"
									onClick={() => setMobileOpen(false)}
								>
									{m.landing_header_startFree()}
								</Link>
							</>
						)}
					</div>
				</div>
			)}
		</header>
	);
}
